import { addMinutes, compareAsc, formatISO, isAfter, max } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { components } from "../../apis/gen";
import {
	ApiError,
	append_sessions,
	create_event,
	get_events,
} from "../../apis/helper";
import type { EventFormData } from "../../components/Staff";
import {
	EventCreationForm,
	EventCreationSuccess,
} from "../../components/Staff";
import { BlockButton } from "../../inJoy-components/Button/BlockButton";
import { Icon } from "../../inJoy-components/Icon/Icon";
import { Tab } from "../../inJoy-components/Tab/Tab";
import { fetchAgentsThunk } from "../../redux/events/events-thunks";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { saveReturnUrl } from "../../utils/auth";
import { EventCard } from "./components";

import type { EventStatus } from "./types";

type Event = components["schemas"]["Event"];

/**
 * Events list page - displays all events with tabs for filtering
 */
const EventsPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const handleAuthError = (err: unknown): boolean => {
		if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
			saveReturnUrl();
			navigate("/login");
			return true;
		}
		return false;
	};
	const { agents } = useAppSelector((state) => state.events);

	const [events, setEvents] = useState<Event[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState(0); // 0: Upcoming, 1: Past, 2: Cancelled, 3: All
	const [pageNumber, setPageNumber] = useState(1);
	const [pageSize] = useState(15);
	const [hasMore, setHasMore] = useState(true);
	const [creating, setCreating] = useState(false);
	const loadMoreRef = useRef<HTMLDivElement | null>(null);
	const [isDebug] = useState(() => localStorage.getItem("debug") === "true");

	// Feature 2: Event Creation Modal state
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [createdEvent, setCreatedEvent] = useState<{
		id: string;
		name: string;
	} | null>(null);

	// Feature 3: Quick Game loading state (per-event)
	const [loadingQuickGame, setLoadingQuickGame] = useState<
		Record<string, boolean>
	>({});

	/**
	 * Helper function to determine event status
	 */
	const getEventStatus = (event: Event): EventStatus => {
		// Feature 1: Check cancelled status via runtime field
		if ((event as any).is_cancelled) return "cancelled";

		const now = new Date();
		const endTime = event.ended_at ? new Date(event.ended_at) : null;

		if (!endTime || !isAfter(now, endTime)) return "upcoming";
		return "past";
	};

	// Filter and sort events by status
	const upcomingEvents = events
		.filter((e) => getEventStatus(e) === "upcoming")
		.sort((a, b) => compareAsc(new Date(a.started_at), new Date(b.started_at)));

	const pastEvents = events.filter((e) => getEventStatus(e) === "past");

	// Feature 1: Cancelled events filter
	const cancelledEvents = events.filter(
		(e) => getEventStatus(e) === "cancelled",
	);

	const tabConfig = [
		{
			events: upcomingEvents,
			status: "upcoming" as EventStatus,
			emptyKey: "events.no_upcoming",
		},
		{
			events: pastEvents,
			status: "past" as EventStatus,
			emptyKey: "events.no_past",
		},
		{
			events: cancelledEvents,
			status: "cancelled" as EventStatus,
			emptyKey: "events.no_cancelled",
		},
		{ events, status: undefined, emptyKey: undefined },
	];

	const currentTab = tabConfig[activeTab] || tabConfig[3];

	const fetchEvents = useCallback(
		async (page: number, isLoadMore = false): Promise<void> => {
			try {
				if (isLoadMore) {
					setLoadingMore(true);
				} else {
					setLoading(true);
				}

				const resData = await get_events({
					number: page,
					size: pageSize,
				});

				const newEvents = resData.data || [];
				const { meta } = resData;

				// Use pagination meta to determine if there are more events
				if (meta) {
					setHasMore(meta.page < meta.page_count);
				} else {
					setHasMore(false);
				}

				if (isLoadMore) {
					setEvents((prev) => [...prev, ...newEvents]);
				} else {
					setEvents(newEvents);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error("⚠️ Error fetching events, err", err);
				if (handleAuthError(err)) return;
				setError(err instanceof Error ? err.message : "Failed to fetch events");
			} finally {
				setLoading(false);
				setLoadingMore(false);
			}
		},
		[pageSize, navigate],
	);

	/**
	 * Parse a 12-hour time string like "1:00 pm" into hours and minutes.
	 */
	const parseTime12h = (
		timeStr: string,
	): { hours: number; minutes: number } => {
		const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
		if (!match) return { hours: 12, minutes: 0 };
		let hours = parseInt(match[1], 10);
		const minutes = parseInt(match[2], 10);
		const period = match[3].toLowerCase();
		if (period === "pm" && hours !== 12) hours += 12;
		if (period === "am" && hours === 12) hours = 0;
		return { hours, minutes };
	};

	/**
	 * Feature 2: Handle event creation form submission
	 */
	const handleCreateEventSubmit = async (
		formData: EventFormData,
	): Promise<void> => {
		try {
			setCreating(true);
			setError(null);

			const baseDate = formData.date;

			const start = parseTime12h(formData.startTime);
			const end = parseTime12h(formData.endTime);

			const startDate = new Date(baseDate);
			startDate.setHours(start.hours, start.minutes, 0, 0);

			const endDate = new Date(baseDate);
			endDate.setHours(end.hours, end.minutes, 0, 0);

			const eventData: Parameters<typeof create_event>[0] = {
				agent_profile_id: formData.agentId,
				calibration: null,
				desc: "",
				name: formData.eventTitle,
				require_booking: false,
				session_metas: [],
				started_at: formatISO(startDate),
				tags: [],
				ended_at: formatISO(endDate),
			};

			if (formData.calibrationChanged && formData.calibrationFile) {
				// User uploaded a new calibration file
				const buffer = await formData.calibrationFile.arrayBuffer();
				const bytes = new Uint8Array(buffer);
				const CHUNK = 0x8000;
				const parts: string[] = [];
				for (let i = 0; i < bytes.length; i += CHUNK) {
					parts.push(String.fromCharCode(...bytes.subarray(i, i + CHUNK)));
				}
				eventData.calibration = btoa(parts.join(""));
			} else if (!formData.calibrationChanged) {
				// User didn't touch calibration — forward the agent's existing one
				const agent = agents.find((a) => a.agent_id === formData.agentId);
				if (agent?.calibration) {
					eventData.calibration = agent.calibration;
				}
			}

			const eventId = await create_event(eventData);

			// Show success screen with QR code
			setCreatedEvent({ id: eventId, name: formData.eventTitle });

			// Refresh events list after successful creation
			setPageNumber(1);
			await fetchEvents(1);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error("⚠️ Error creating event:", err);
			if (handleAuthError(err)) return;
			setError(err instanceof Error ? err.message : "Failed to create event");
		} finally {
			setCreating(false);
		}
	};

	/**
	 * Feature 3: Quick Game - append a new session to an event
	 */
	const handleQuickGame = async (event: Event): Promise<void> => {
		try {
			setLoadingQuickGame((prev) => ({ ...prev, [event.event_id]: true }));

			const now = new Date();
			const BUFFER_MINUTES = 2;

			// Find the latest session end time
			let latestSessionEnd = new Date(event.started_at);
			if (event.sessions && event.sessions.length > 0) {
				for (const session of event.sessions) {
					const sessionEnd = session.ended_at
						? new Date(session.ended_at)
						: new Date(0);
					latestSessionEnd = max([latestSessionEnd, sessionEnd]);
				}
			}

			// Check event window - new session must fit within event end time
			const eventEnd = event.ended_at
				? new Date(event.ended_at)
				: new Date(8640000000000000); // Max possible date

			const sessionStartTime = max([
				now,
				addMinutes(latestSessionEnd, BUFFER_MINUTES),
			]);

			if (!isAfter(eventEnd, sessionStartTime)) {
				setError("Cannot add game: event time window has ended");
				return;
			}

			// Use the simple name/desc variant of AppendSessionPayload
			await append_sessions(event.event_id, {
				name: `Game ${(event.sessions?.length || 0) + 1}`,
				desc: "",
				started_at: sessionStartTime.toISOString(),
				ended_at: new Date(
					sessionStartTime.getTime() + 60 * 12 * 1000,
				).toISOString(),
				appender_email: null,
			});

			// Refresh events list
			setPageNumber(1);
			await fetchEvents(1);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error("⚠️ Error adding quick game:", err);
			if (handleAuthError(err)) return;
			setError(err instanceof Error ? err.message : "Failed to add game");
		} finally {
			setLoadingQuickGame((prev) => ({ ...prev, [event.event_id]: false }));
		}
	};

	/**
	 * Close create modal and reset state
	 */
	const closeCreateModal = () => {
		setShowCreateModal(false);
		setCreatedEvent(null);
	};

	useEffect(() => {
		if (showCreateModal) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}
		return () => {
			document.body.style.overflow = "unset";
		};
	}, [showCreateModal]);

	// Fetch agents on mount (for creation form)
	useEffect(() => {
		dispatch(fetchAgentsThunk());
	}, [dispatch]);

	// Initial fetch
	useEffect(() => {
		fetchEvents(1);
	}, [fetchEvents]);

	// Load more when pageNumber changes (except initial load)
	useEffect(() => {
		if (pageNumber > 1) {
			fetchEvents(pageNumber, true);
		}
	}, [pageNumber, fetchEvents]);

	// Refs for IntersectionObserver callback to avoid recreation
	const hasMoreRef = useRef(hasMore);
	const loadingRef = useRef(loading);
	const loadingMoreRef = useRef(loadingMore);
	const errorRef = useRef(error);
	hasMoreRef.current = hasMore;
	loadingRef.current = loading;
	loadingMoreRef.current = loadingMore;
	errorRef.current = error;

	// Re-attach observer when sentinel mounts/unmounts (gated by loading/error)
	useEffect(() => {
		const el = loadMoreRef.current;
		if (!el) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (
					entries[0].isIntersecting &&
					hasMoreRef.current &&
					!loadingRef.current &&
					!loadingMoreRef.current &&
					!errorRef.current
				) {
					setPageNumber((prev) => prev + 1);
				}
			},
			{ threshold: 0.1 },
		);
		observer.observe(el);

		return () => observer.disconnect();
	}, [loading, error]);

	return (
		<div className="pt-8 px-[calc(50vw-600px)] pb-10 min-h-screen flex flex-col bg-color-Grey50">
			{/* ==== TITLE ==== */}
			<div className="flex items-center justify-between mb-5">
				<h1 className="text-4xl font-medium text-color-Grey900">
					{t("events.title")}
				</h1>
				{isDebug && (
					<BlockButton
						type="Primary"
						width="160px"
						icon="plus_outline"
						loadingIcon="spinner_outline"
						size="Large"
						onClick={() => setShowCreateModal(true)}
						loading={creating}
						disabled={creating}
					>
						{t("events.create_event")}
					</BlockButton>
				)}
			</div>

			{/* ==== Tab Navigation ==== */}
			<div className="mt-7">
				<Tab
					className="w-full shadow-transparent !bg-color-Grey50"
					tabs={[
						t("events.tabs.upcoming"),
						t("events.tabs.past"),
						t("events.tabs.cancelled"),
						t("events.tabs.all"),
					]}
					activeTab={activeTab}
					setActiveTab={setActiveTab}
				/>
			</div>

			{/* ==== Fetch Status ==== */}
			{loading && (
				<div className="text-center py-8">
					<p className="text-gray-600">{t("events.loading")}</p>
				</div>
			)}

			{error && (
				<div className="text-center py-8">
					<p className="text-red-600">{t("events.error_loading", { error })}</p>
				</div>
			)}

			{!loading && !error && events.length === 0 && (
				<div className="text-center py-8">
					<p className="text-gray-600">{t("events.no_events")}</p>
				</div>
			)}

			{/* ==== Tabbed Content ==== */}
			{!loading && !error && (
				<div className="mt-5">
					{currentTab.events.length > 0 ? (
						<div className="flex flex-col gap-5">
							{currentTab.events.map((event) => (
								<EventCard
									key={event.event_id}
									event={event}
									status={currentTab.status || getEventStatus(event)}
									onQuickGame={handleQuickGame}
									isAddingGame={!!loadingQuickGame[event.event_id]}
								/>
							))}
						</div>
					) : (
						currentTab.emptyKey && (
							<div className="text-center py-8">
								<p className="text-gray-600">{t(currentTab.emptyKey)}</p>
							</div>
						)
					)}

					{/* Infinite scroll sentinel and loading indicator */}
					<div
						className="mt-10 flex flex-col items-center gap-y-10"
						ref={loadMoreRef}
					>
						{loadingMore && (
							<Icon
								iconName="spinner_outline"
								size="XXLarge"
								color="#999999"
								loading={true}
								onClick={() => undefined}
							/>
						)}
						{!hasMore && currentTab.events.length > 0 && (
							<p className="text-base text-color-Grey700">
								{t("events.reached_end")}
							</p>
						)}
					</div>
				</div>
			)}

			{/* ==== Event Creation Modal ==== */}
			{showCreateModal && isDebug && (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
					onClick={(e) => {
						if (e.target === e.currentTarget) closeCreateModal();
					}}
				>
					{createdEvent ? (
						<EventCreationSuccess
							eventId={createdEvent.id}
							eventName={createdEvent.name}
							onClose={closeCreateModal}
						/>
					) : (
						<EventCreationForm
							agents={agents}
							isLoading={creating}
							onSubmit={handleCreateEventSubmit}
							onClose={closeCreateModal}
							onRefreshAgents={() => dispatch(fetchAgentsThunk())}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export { EventsPage };
